// Overrides Backstage's default `app/layout` extension (which renders
// <SidebarPage>{nav}{content}</SidebarPage>) with our fully custom shell. The
// extension keeps the same id (namespace 'app' from the module + name 'layout')
// so it replaces the built-in; it still declares the `nav` input so the core
// `app/nav` extension attaches without error, but our shell renders its own
// sidebar and ignores it.
import {
  coreExtensionData,
  createExtension,
  createExtensionInput,
  createFrontendModule,
  PageLayout,
} from '@backstage/frontend-plugin-api';
import { SwappableComponentBlueprint } from '@backstage/plugin-app-react';
import type { ReactNode } from 'react';
import { AppShell } from './AppShell';

// Backstage's default `core-page-layout` swappable wraps every routed page in a
// <PluginHeader> that shows the plugin title (falls back to the pluginId "app")
// and its icon — that stray "app" bar above our pages. Our pages render their
// own H1/toolbar, so override the layout to render page content only.
const pageLayout = SwappableComponentBlueprint.make({
  name: 'core-page-layout',
  params: define =>
    define({
      component: PageLayout,
      loader: () => (props: { children?: ReactNode }) => <>{props.children}</>,
    }),
});

const appLayout = createExtension({
  name: 'layout',
  attachTo: { id: 'app/root', input: 'children' },
  inputs: {
    nav: createExtensionInput([coreExtensionData.reactElement], {
      singleton: true,
    }),
    content: createExtensionInput([coreExtensionData.reactElement], {
      singleton: true,
    }),
  },
  output: [coreExtensionData.reactElement],
  factory: ({ inputs }) => [
    coreExtensionData.reactElement(
      <AppShell>{inputs.content.get(coreExtensionData.reactElement)}</AppShell>,
    ),
  ],
});

export const shellModule = createFrontendModule({
  pluginId: 'app',
  extensions: [appLayout, pageLayout],
});
